import { Typography } from '@euler/app/components/typography';
import {
  ApdmImportState,
  apdmImportStateFromView,
} from '@euler/functions/apdm/import/importState';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useTheme } from 'styled-components';
import {
  ReviewAction,
  ReviewActionTone,
  ReviewSection,
} from '../../ReviewControls';
import { ImportRow } from './ImportRow';

const kPageSize = 20;

/** The store's earlier imports; opening one shows it above. */
export function ImportsList({
  selectedImportId,
  reloadKey,
  onOpen,
}: {
  selectedImportId: string | null;
  /** Changes when an import was added, removed or moved on. */
  reloadKey: string;
  onOpen: (importId: string) => void;
}) {
  const { apdmService } = useServiceFactory();
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  const [items, setItems] = useState<ApdmImportState[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const load = useCallback(
    async (afterImportId: string | null) => {
      setLoading(true);
      setFailed(false);
      try {
        const page = await apdmService.listImports({
          afterImportId,
          limit: kPageSize,
        });
        const states = page.items.map(apdmImportStateFromView);
        setItems(current => (afterImportId ? [...current, ...states] : states));
        setNext(page.nextAfterImportId);
      } catch (error) {
        console.error('APDM import list failed', error);
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [apdmService],
  );
  useEffect(() => {
    void load(null);
  }, [load, reloadKey]);
  return (
    <ReviewSection title="导入记录">
      {items.map(item => (
        <ImportRow
          key={item.id}
          state={item}
          selected={item.id === selectedImportId}
          onOpen={() => onOpen(item.id)}
        />
      ))}
      {!loading && !failed && !items.length ? (
        <Typography.Text size={rpx(13)} color={theme.label.color}>
          还没有导入过文件。
        </Typography.Text>
      ) : null}
      {failed ? (
        <View style={{ marginTop: rpx(8) }}>
          <Typography.Text
            accessibilityRole="alert"
            size={rpx(13)}
            color={theme.colors.status.danger}
          >
            列表没有加载出来，请重试。
          </Typography.Text>
        </View>
      ) : null}
      {next ? (
        <ReviewAction
          title="更多"
          tone={ReviewActionTone.Secondary}
          loading={loading}
          onPress={() => void load(next)}
        />
      ) : null}
    </ReviewSection>
  );
}
